import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { message, Modal } from "antd";
import * as employeeServices from "features/employee/services/employeeApi";
import useOptions from "features/masterData/hooks/useOptions";
import { dataURLtoFile, generateRandomString, groupBy } from "utils/helper";
import * as services from "../services/documentApi";
import {
  setDocument,
  setIsLoading,
  setIsLoadingData,
  setSignature,
} from "../slices/companySlice";
import { t } from "i18next";

function toInitialValues(initialValues = {}) {
  const { documentTemplateResponse = {}, documentSignatureResponse = [] } =
    initialValues;
  const { typeOfSlip = {} } = documentTemplateResponse;
  const groupedSignature = groupBy(
    documentSignatureResponse,
    "documentTypeName"
  );
  const modifiedValues = {
    ...documentTemplateResponse,
    typeOfSlip: typeOfSlip.id || null,
    paySlipSize: documentTemplateResponse.paySlipSize || null,
    documentSignatureResponse: groupedSignature,
  };
  return modifiedValues;
}

function base64ToArrayBuffer(base64) {
  var binaryString = atob(base64);
  var bytes = new Uint8Array(binaryString.length);
  for (var i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function toParams(values = {}) {
  function sealLogoFn() {
    if (typeof values.sealLogo == 'string') {
      return dataURLtoFile("data:image/png;base64," + values.sealLogo,
        "example.png"
      )
    } else {
      return values.sealLogo
    }
  }

  const modifiedValues = {
    requestId: values.requestId,
    companyId: values.companyId,
    documentTemplateId: values.documentTemplateId || null,
    typeOfSlip: values.typeOfSlip,
    paySlipSize: values.paySlipSize,
    sealLogo: sealLogoFn()
  };
  return modifiedValues;
}

function useCompanyDocument(props) {

  const { onEdit = () => { }, onCancel = () => { } } = props;
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.company.isLoading);
  const isLoadingData = useSelector((state) => state.company.isLoadingData);

  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentDocument = useSelector(
    (state) => state.company.currentDocument || {}
  );
  const currentSignature = useSelector(
    (state) => state.company.currentSignature || {}
  );
  const [selectedDocumentSignature, setDocumentSignature] = useState({});
  const [isCreateCertificate, setIsCreateCertificate] = useState(false);
  const [isEditCertificate, setIsEditCertificate] = useState(false);
  const [isViewCertificate, setIsViewCertificate] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const { typeOfSlipOptions = [], getTypeOfSlipOptions } = useOptions();

  const initialValues = useMemo(() => {
    return toInitialValues(currentDocument);
  }, [currentDocument]);

  const modifiedSignature = useMemo(() => {
    const modifiedValues = {
      ...currentSignature,
      signature: currentSignature.signature
        ? dataURLtoFile(
          "data:image/png;base64, " + currentSignature.signature,
          "example.png"
        )
        : null,
    };
    return modifiedValues;
  }, [currentSignature]);

  const modifiedEmployeeOptions = useMemo(() => {
    const modifiedOptions = employeeOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.fullNameEn : opt.fullName,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n, employeeOptions]);

  const getDocument = useCallback(async () => {
    try {
      dispatch(setIsLoadingData(true));
      const values = {
        requestId: generateRandomString(),
        companyId: currentCompany,
      };
      const response = await services.getDocument(values);
      dispatch(setDocument(response.data));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoadingData(false));
    }
  }, [currentCompany, dispatch]);

  const createDocumentTemplate = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.createDocumentTemplate(toParams(values));
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel]
  );

  const editDocumentTemplate = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        // toParams(values)
        await services.editDocumentTemplate(toParams(values));
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel]
  );

  const onSubmit = useCallback(
    async (values) => {
      const modifiedValues = {
        ...values,
        requestId: generateRandomString(),
        companyId: currentCompany,
      };
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>{t("dialog.label.confirm_edit_doc_company")}</p>
          </>
        ),
        onOk() {
          const { documentTemplateResponse = {} } = currentDocument;
          if (documentTemplateResponse.documentTemplateId) {
            editDocumentTemplate(modifiedValues);
          } else {
            createDocumentTemplate(modifiedValues);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [
      currentCompany,
      currentDocument,
      createDocumentTemplate,
      editDocumentTemplate,
    ]
  );

  const onCreateSignature = useCallback((value = {}) => {
    const modifiedValue = {
      ...value,
      authorized: null,
      signature: null,
    };
    setDocumentSignature(modifiedValue);
    setIsCreateCertificate(true);
  }, []);

  const onCancelSignature = useCallback(() => {
    setDocumentSignature({});
    setIsCreateCertificate(false);
    setIsEditCertificate(false);
    setIsViewCertificate(false);
  }, []);

  const onSubmitCreateSignature = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          documentTypeId: values.documentTypeId,
          authorized: values.authorized,
          signature: values.signature,
        };
        const i = await services.createDocumentSignature(modifiedValues);
        if (i.data?.code === '400') {
          Modal.error({
            content: i.data?.details[0].value,
            okText: t('message.ok'),
          });
        } else {
          getDocument();
          message.success(t("common.success"))
          onCancelSignature();
        }
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
        setIsCreateCertificate(false);
        setIsEditCertificate(false);
        setIsViewCertificate(false);
      }
    },
    [currentCompany, dispatch, onCancelSignature, getDocument]
  );

  const onEditSignature = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          documentSignatureId: values.documentSignatureId,
        };
        const response = await services.getDocumentSignature(modifiedValues);
        const { data = {} } = response;
        const { authorized = {} } = data;
        const modifiedData = {
          ...data,
          authorized: authorized.id,
          documentTypeId: values.documentTypeId,
          documentTypeName:
            i18n.language === "en"
              ? values.documentTypeNameEn
              : values.documentTypeName,
        };
        dispatch(setSignature(modifiedData));
        setIsEditCertificate(true);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, i18n.language, dispatch]
  );

  const onSubmitEditSignature = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          documentSignatureId: values.documentSignatureId,
          documentTypeId: values.documentTypeId,
          authorized: values.authorized,
          signature: values.signature,
        };
        await services.editDocumentSignature(modifiedValues);
        getDocument();
        message.success(t("common.success"))
        onCancelSignature();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancelSignature, getDocument]
  );

  const onViewignature = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          documentSignatureId: values.documentSignatureId,
        };
        const response = await services.getDocumentSignature(modifiedValues);
        const { data = {} } = response;
        const { authorized = {} } = data;
        const modifiedData = {
          ...data,
          authorized: authorized.id,
          documentTypeId: values.documentTypeId,
          documentTypeName:
            i18n.language === "en"
              ? values.documentTypeNameEn
              : values.documentTypeName,
        };
        dispatch(setSignature(modifiedData));
        setIsViewCertificate(true);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, i18n.language, dispatch]
  );

  const getEmployeeOptions = useCallback(async () => {
    if (typeof currentCompany !== "object") {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await employeeServices.getDropDown(values);
        setEmployeeOptions(response.data);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    }
  }, [currentCompany, dispatch]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getDocument();
    }
  }, [dispatch, currentCompany, getDocument]);

  useEffect(() => {
    getEmployeeOptions();
    getTypeOfSlipOptions();
  }, [dispatch, getEmployeeOptions, getTypeOfSlipOptions]);


  return {
    isLoading,
    isLoadingData,
    initialValues,
    currentDocument,
    typeOfSlipOptions,
    isViewCertificate,
    isEditCertificate,
    isCreateCertificate,
    selectedDocumentSignature,
    currentSignature: modifiedSignature,
    employeeOptions: modifiedEmployeeOptions,
    onEdit,
    onCancel,
    onSubmit,
    setIsEditCertificate,
    setIsCreateCertificate,
    onCreateSignature,
    onCancelSignature,
    onSubmitCreateSignature,
    onEditSignature,
    onSubmitEditSignature,
    onView: onViewignature,
  };
}

export default useCompanyDocument;
